import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  ChevronUp,
  ChevronDown,
  Building2,
  MapPin,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import type { KVAttendanceSummary, TableSortConfig, SortField } from '../../types/analytics';

interface KVTableProps {
  data: KVAttendanceSummary[];
  isLoading?: boolean;
  sortConfig?: TableSortConfig;
  onSort?: (field: SortField) => void;
  onRowClick?: (kv: KVAttendanceSummary) => void;
  emptyMessage?: string;
}

const getAttendanceColor = (percent: number): string => {
  if (percent >= 90) return 'text-green-600 bg-green-50';
  if (percent >= 75) return 'text-yellow-600 bg-yellow-50';
  if (percent >= 50) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
};

const getAttendanceIcon = (percent: number) => {
  if (percent >= 90) return <TrendingUp className="w-3 h-3" />;
  if (percent >= 50) return <Minus className="w-3 h-3" />;
  return <TrendingDown className="w-3 h-3" />;
};

interface SortableHeaderProps {
  label: string;
  field: SortField;
  sortConfig?: TableSortConfig;
  onSort?: (field: SortField) => void;
  align?: 'left' | 'center' | 'right';
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  field,
  sortConfig,
  onSort,
  align = 'left'
}) => {
  const isActive = sortConfig?.field === field;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
      <button
        onClick={() => onSort?.(field)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${alignClass}`}
      >
        {label}
        <span className="flex flex-col">
          <ChevronUp
            className={`w-3 h-3 -mb-1 ${isActive && sortConfig?.order === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
          />
          <ChevronDown
            className={`w-3 h-3 ${isActive && sortConfig?.order === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
          />
        </span>
      </button>
    </th>
  );
};

const LoadingRow: React.FC = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-8"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-48"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
    <td className="px-4 py-4"><div className="h-8 bg-gray-200 rounded w-20"></div></td>
  </tr>
);

export const KVTable: React.FC<KVTableProps> = ({
  data,
  isLoading = false,
  sortConfig,
  onSort,
  onRowClick,
  emptyMessage = 'No KV schools found'
}) => {
  const navigate = useNavigate();

  const handleRowClick = (kv: KVAttendanceSummary) => {
    if (onRowClick) {
      onRowClick(kv);
    } else {
      navigate(`/attendance/analytics/kv/${kv.kvId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">KV School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Region</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">State</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Staff</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Present</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(10)].map((_, idx) => (
                <LoadingRow key={idx} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">{emptyMessage}</p>
        <p className="text-gray-400 text-sm mt-2">Try adjusting your filters or date selection</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                #
              </th>
              <SortableHeader label="KV School" field="kvName" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Region" field="roName" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="State" field="stateName" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label="Staff" field="totalStaff" sortConfig={sortConfig} onSort={onSort} align="center" />
              <SortableHeader label="Present" field="presentCount" sortConfig={sortConfig} onSort={onSort} align="center" />
              <SortableHeader label="Attendance" field="attendancePercent" sortConfig={sortConfig} onSort={onSort} align="center" />
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((kv, index) => (
              <tr
                key={kv.kvId}
                onClick={() => handleRowClick(kv)}
                className="hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-4 text-sm text-gray-500">
                  {index + 1}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-start gap-2">
                    <div className="p-1.5 bg-blue-50 rounded-lg mt-0.5">
                      <Building2 className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{kv.kvName}</p>
                      {kv.districtName && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" />
                          {kv.districtName}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {kv.roName || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {kv.stateName || '-'}
                </td>
                <td className="px-4 py-4 text-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{kv.totalStaff}</p>
                    <p className="text-xs text-gray-500">
                      {kv.regularStaff}R / {kv.contractualStaff}C
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4 text-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{kv.presentCount}</p>
                    <p className="text-xs text-gray-500">
                      <span className="text-red-500">{kv.absentCount}A</span>
                      {kv.noResponseCount > 0 && (
                        <span className="text-yellow-500 ml-1">/ {kv.noResponseCount}NR</span>
                      )}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${getAttendanceColor(kv.attendancePercent)}`}>
                    {getAttendanceIcon(kv.attendancePercent)}
                    {kv.attendancePercent}%
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(kv);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KVTable;
